import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Upload } from 'lucide-react';

import {
  designarCertificadoServico,
  uploadCertificadoServico,
  uploadOtpCertificadoServico,
} from '@/api/certificadoServico';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';

interface CadastrarServicoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Etapa = 'certificado' | 'otp' | 'designar';

export function CadastrarServicoDialog({ open, onOpenChange }: CadastrarServicoDialogProps) {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [etapa, setEtapa] = useState<Etapa>('certificado');
  const [busy, setBusy] = useState(false);

  // Passo 1
  const [file, setFile] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  // Resultado do passo 1
  const [cpf, setCpf] = useState('');
  const [titular, setTitular] = useState('');
  // Passo 2
  const [qrFile, setQrFile] = useState<File | null>(null);
  // Passo 3
  const [rotulo, setRotulo] = useState('');

  const fechar = () => {
    setEtapa('certificado');
    setFile(null);
    setSenha('');
    setCpf('');
    setTitular('');
    setQrFile(null);
    setRotulo('');
    onOpenChange(false);
  };

  const handleUploadCert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !senha) {
      showError('Selecione o arquivo .p12/.pfx e informe a senha.');
      return;
    }
    setBusy(true);
    try {
      const res = await uploadCertificadoServico(file, senha);
      setCpf(res.cpf);
      setTitular(res.titular_nome ?? '');
      setSenha('');
      setEtapa('otp');
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setBusy(false);
    }
  };

  const handleUploadOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!qrFile) {
      showError('Selecione a imagem do QR Code do OTP.');
      return;
    }
    setBusy(true);
    try {
      await uploadOtpCertificadoServico(cpf, qrFile);
      setEtapa('designar');
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setBusy(false);
    }
  };

  const handleDesignar = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await designarCertificadoServico({ cpf, rotulo: rotulo || null });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'certificados-servico'] });
      showSuccess('Certificado de serviço cadastrado e designado.');
      fechar();
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : fechar())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cadastrar novo certificado de serviço</DialogTitle>
          <DialogDescription>
            Três passos: enviar o certificado (.p12/.pfx), conectar o OTP da PDPJ (QR Code) e
            confirmar a designação.
          </DialogDescription>
        </DialogHeader>

        {etapa === 'certificado' && (
          <form onSubmit={handleUploadCert} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cad-arquivo">Arquivo do certificado</Label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => document.getElementById('cad-arquivo')?.click()}
                >
                  <Upload className="h-4 w-4" />
                  {file ? file.name : 'Selecionar (.p12/.pfx)'}
                </Button>
                <input
                  id="cad-arquivo"
                  type="file"
                  accept=".p12,.pfx"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cad-senha">Senha do certificado</Label>
              <Input
                id="cad-senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={fechar}>
                Cancelar
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? <Spinner size={16} className="text-inherit mr-1.5" /> : null}
                Enviar certificado
              </Button>
            </DialogFooter>
          </form>
        )}

        {etapa === 'otp' && (
          <form onSubmit={handleUploadOtp} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Certificado de <strong>{titular || 'titular'}</strong> (CPF {cpf}) enviado. Agora
              envie o QR Code do OTP da PDPJ.
            </p>
            <div className="space-y-2">
              <Label htmlFor="cad-qr">Imagem do QR Code (OTP)</Label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => document.getElementById('cad-qr')?.click()}
                >
                  <Upload className="h-4 w-4" />
                  {qrFile ? qrFile.name : 'Selecionar imagem'}
                </Button>
                <input
                  id="cad-qr"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setQrFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={fechar}>
                Cancelar
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? <Spinner size={16} className="text-inherit mr-1.5" /> : null}
                Conectar OTP
              </Button>
            </DialogFooter>
          </form>
        )}

        {etapa === 'designar' && (
          <form onSubmit={handleDesignar} className="space-y-4">
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Certificado e OTP prontos. Confirme a designação.
            </p>
            <div className="space-y-2">
              <Label htmlFor="cad-rotulo">Rótulo (opcional)</Label>
              <Input
                id="cad-rotulo"
                placeholder="Ex.: Certificado institucional"
                value={rotulo}
                onChange={(e) => setRotulo(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={fechar}>
                Cancelar
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? <Spinner size={16} className="text-inherit mr-1.5" /> : null}
                Designar
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
